import { Link } from 'react-router-dom';
import type { StepDraft } from '@/components/marketing/campaigns/SequenceStepsEditor';
import { variantBApplies } from '@/components/marketing/campaigns/step-rules';
import { Button } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';

export type BuilderSection = 'details' | 'audience' | 'sender' | 'content' | 'review';

export interface ReviewChecklistProps {
  name: string;
  kind: 'broadcast' | 'sequence';
  emailAccountId: number | null;
  includeCount: number;
  steps: StepDraft[];
  abEnabled: boolean;
  dirty: boolean;
  recipientEstimate: number | null;
  finishLabel: string | null;
  /** Problems the server named on the last schedule attempt (`422 MARKETING_CAMPAIGN_INCOMPLETE`). */
  serverProblems: { path: string; message: string }[];
  onJump(section: BuilderSection): void;
}

interface Item {
  key: string;
  ok: boolean;
  label: string;
  section: BuilderSection;
}

function sectionOfPath(path: string): BuilderSection {
  if (path === 'name') return 'details';
  if (path.startsWith('audience') || path.startsWith('include') || path.startsWith('exclude')) return 'audience';
  if (path === 'emailAccountId' || path === 'fromNameOverride' || path === 'replyToOverride') return 'sender';
  if (path.startsWith('steps') || path.startsWith('ab')) return 'content';
  return 'review';
}

const SECTION_LABEL: Record<BuilderSection, string> = {
  details: 'Details',
  audience: 'Audience',
  sender: 'Sender',
  content: 'Content',
  review: 'Review',
};

function Mark({ ok }: { ok: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={
        ok
          ? 'grid h-4 w-4 shrink-0 place-items-center rounded-full bg-success-soft text-success'
          : 'grid h-4 w-4 shrink-0 place-items-center rounded-full bg-warning-soft text-warning'
      }
    >
      <svg viewBox="0 0 24 24" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="3">
        {ok ? <path d="m5 12 5 5L20 7" /> : <path d="M12 7v6m0 4h.01" />}
      </svg>
    </span>
  );
}

/** What still stands between this draft and a send, computed from what is on screen. */
export function ReviewChecklist({
  name,
  kind,
  emailAccountId,
  includeCount,
  steps,
  abEnabled,
  dirty,
  recipientEstimate,
  finishLabel,
  serverProblems,
  onJump,
}: ReviewChecklistProps) {
  const items: Item[] = [
    { key: 'name', ok: name.trim() !== '', label: 'Has a name', section: 'details' },
    { key: 'audience', ok: includeCount > 0, label: 'Sends to at least one list', section: 'audience' },
    { key: 'sender', ok: emailAccountId !== null, label: 'Has an email account to send from', section: 'sender' },
  ];

  const shownSteps = kind === 'broadcast' ? steps.slice(0, 1) : steps;
  shownSteps.forEach((step, stepIndex) => {
    const prefix = kind === 'sequence' ? `Step ${stepIndex + 1}: ` : '';
    const ab = variantBApplies(kind, abEnabled, stepIndex);
    for (const variant of step.variants) {
      if (variant.label === 'B' && !ab) continue;
      const which = ab ? ` (${variant.label})` : '';
      items.push({
        key: `subject-${step.key}-${variant.key}`,
        ok: variant.subject.trim() !== '',
        // A broadcast has no "Step N: " prefix, so the bare noun would start
        // the line in lower case beside "Has a name" — say it as a sentence.
        label: prefix ? `${prefix}subject line${which}` : `Has a subject line${which}`,
        section: 'content',
      });
      items.push({
        key: `template-${step.key}-${variant.key}`,
        ok: variant.templateId !== null,
        label: prefix ? `${prefix}template${which}` : `Has a template${which}`,
        section: 'content',
      });
    }
  });
  if (shownSteps.length === 0) {
    items.push({ key: 'steps', ok: false, label: 'Has an email to send', section: 'content' });
  }
  if (variantBApplies(kind, abEnabled, 0)) {
    const first = steps[0];
    items.push({
      key: 'ab-b',
      ok: Boolean(first?.variants.some((v) => v.label === 'B')),
      label: 'Has a second subject line to test',
      section: 'content',
    });
  }
  items.push({ key: 'saved', ok: !dirty, label: 'All changes saved', section: 'review' });

  const open = items.filter((i) => !i.ok).length;

  return (
    <div className="grid min-w-0 gap-4">
      <ul className="m-0 grid list-none gap-1.5 p-0 text-sm">
        {items.map((item) => (
          <li key={item.key} className="flex min-w-0 items-center gap-2">
            <Mark ok={item.ok} />
            <span className={item.ok ? 'text-fg-secondary' : 'text-fg'}>{item.label}</span>
            {!item.ok && item.section !== 'review' && (
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => onJump(item.section)}>
                Go to {SECTION_LABEL[item.section]}
              </Button>
            )}
          </li>
        ))}
      </ul>

      {serverProblems.length > 0 && (
        <Notice tone="danger" role="alert" title="Not ready to schedule">
          <ul className="m-0 grid list-none gap-1 p-0">
            {serverProblems.map((problem, index) => {
              const section = sectionOfPath(problem.path);
              return (
                <li key={`${problem.path}-${index}`} className="flex flex-wrap items-center gap-x-2 [overflow-wrap:anywhere]">
                  <span>{problem.message}</span>
                  {section !== 'review' ? (
                    <button
                      type="button"
                      className="underline underline-offset-2"
                      onClick={() => onJump(section)}
                    >
                      {SECTION_LABEL[section]}
                    </button>
                  ) : problem.path.startsWith('settings') ? (
                    <Link className="underline underline-offset-2" to="/marketing/settings">
                      Marketing settings
                    </Link>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </Notice>
      )}

      <dl className="m-0 grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
        <div>
          <dt className="text-fg-muted">Recipients</dt>
          <dd className="m-0 text-fg-secondary tabular">
            {recipientEstimate === null ? 'Not estimated yet' : recipientEstimate.toLocaleString()}
          </dd>
        </div>
        <div>
          <dt className="text-fg-muted">Estimated finish</dt>
          <dd className="m-0 text-fg-secondary">{finishLabel ?? '—'}</dd>
        </div>
      </dl>

      <p className="m-0 sr-only" role="status">
        {open === 0 ? 'Ready to schedule' : `${open} ${open === 1 ? 'item' : 'items'} to finish`}
      </p>
    </div>
  );
}
